import { Language } from '../types/kundali';

export interface NatalPromiseTierInfo {
  tierEn: string;
  tierHi: string;
  badgeClass: string;
  textClass: string;
  bgClass: string;
  borderClass: string;
}

export interface TimingConfluenceInfo {
  stars: string;
  labelEn: string;
  labelHi: string;
  badgeClass: string;
  textColor: string;
}

/**
 * Standardized V5.15 Natal Promise Qualitative Tiers
 * 90–100 EXCEPTIONAL
 * 75–89 HIGHLY SUPPORTIVE
 * 50–74 MODERATE / BALANCED
 * 35–49 CHALLENGING / REQUIRES EFFORT
 * 0–34 CONSTRAINED
 */
export function getNatalPromiseTier(score: number, lang: Language = 'en'): NatalPromiseTierInfo {
  if (score >= 90) {
    return {
      tierEn: 'EXCEPTIONAL SUPPORT',
      tierHi: 'अत्यंत शुभ एवं प्रबल',
      badgeClass: 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700',
      textClass: 'text-emerald-700 dark:text-emerald-300',
      bgClass: 'bg-emerald-500',
      borderClass: 'border-emerald-500'
    };
  }
  if (score >= 75) {
    return {
      tierEn: 'STRONG SUPPORT',
      tierHi: 'प्रबल अनुकूलता',
      badgeClass: 'bg-teal-100 dark:bg-teal-950/70 text-teal-800 dark:text-teal-200 border border-teal-300 dark:border-teal-700',
      textClass: 'text-teal-700 dark:text-teal-300',
      bgClass: 'bg-teal-500',
      borderClass: 'border-teal-500'
    };
  }
  if (score >= 50) {
    return {
      tierEn: 'BALANCED & PROMISING',
      tierHi: 'संतुलित एवं अनुकूल फल',
      badgeClass: 'bg-astro-primary/15 text-astro-primary border border-astro-primary/30',
      textClass: 'text-astro-primary',
      bgClass: 'bg-astro-primary',
      borderClass: 'border-astro-primary'
    };
  }
  if (score >= 35) {
    return {
      tierEn: 'REQUIRES CONSCIOUS EFFORT',
      tierHi: 'प्रयत्न व धैर्य अपेक्षित',
      badgeClass: 'bg-astro-accent/15 dark:bg-astro-accent/30 text-astro-accent dark:text-[#B7A17A] border border-astro-accent/30',
      textClass: 'text-astro-accent dark:text-[#B7A17A]',
      bgClass: 'bg-astro-accent',
      borderClass: 'border-astro-accent'
    };
  }
  return {
    tierEn: 'NEEDS MINDFUL CARE',
    tierHi: 'सतर्कता व मार्गदर्शन उपयोगी',
    badgeClass: 'bg-rose-100 dark:bg-rose-950/70 text-rose-800 dark:text-rose-200 border border-rose-300 dark:border-rose-700',
    textClass: 'text-rose-700 dark:text-rose-300',
    bgClass: 'bg-rose-500',
    borderClass: 'border-rose-500'
  };
}

/**
 * Standardized V5.15 Timing Confluence Stars (Warm client-first presentation)
 * ★★★★ Strong Alignment (score >= 80)
 * ★★★☆ Favourable Time (score 60–79)
 * ★★☆☆ Developing Time (score < 60)
 */
export function getTimingConfluence(confidenceScore: number, lang: Language = 'en'): TimingConfluenceInfo {
  if (confidenceScore >= 80) {
    return {
      stars: '★★★★',
      labelEn: 'Strong Alignment',
      labelHi: 'विशेष शुभ समय',
      badgeClass: 'bg-emerald-500 text-white shadow-xs',
      textColor: 'text-emerald-600 dark:text-emerald-400'
    };
  }
  if (confidenceScore >= 60) {
    return {
      stars: '★★★☆',
      labelEn: 'Favourable Time',
      labelHi: 'अनुकूल समय',
      badgeClass: 'bg-astro-primary text-white shadow-2xs',
      textColor: 'text-astro-primary'
    };
  }
  return {
    stars: '★★☆☆',
    labelEn: 'Developing Time',
    labelHi: 'विकासशील समय',
    badgeClass: 'bg-slate-600 text-white shadow-xs',
    textColor: 'text-slate-600 dark:text-slate-400'
  };
}

/**
 * Calculate Projected Native Age Window (e.g., "Approx. Age 34.4y – 34.9y")
 */
export function calculateProjectedAgeWindow(
  dobString: string,
  startDateStr: string,
  endDateStr: string,
  lang: Language = 'en'
): string {
  try {
    const dob = new Date(dobString);
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);

    if (isNaN(dob.getTime()) || isNaN(start.getTime()) || isNaN(end.getTime())) {
      return '';
    }

    const startAgeYears = (start.getTime() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    const endAgeYears = (end.getTime() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);

    if (startAgeYears < 0) return '';

    const startFormatted = startAgeYears.toFixed(1);
    const endFormatted = endAgeYears.toFixed(1);

    if (lang === 'hi') {
      return `लगभग आयु: ${startFormatted} से ${endFormatted} वर्ष`;
    }
    return `Approx. Age: ${startFormatted}y – ${endFormatted}y`;
  } catch {
    return '';
  }
}

/**
 * Concise Varga Role Microcopy Note
 */
export function getVargaRoleNote(vargaCode: string, lang: Language = 'en'): { roleEn: string; roleHi: string } {
  switch (vargaCode) {
    case 'D1':
      return {
        roleEn: 'Natal foundation and overall life pattern.',
        roleHi: 'शारीरिक आधार, स्वास्थ्य एवं समग्र जीवन प्रारब्ध।'
      };
    case 'D9':
      return {
        roleEn: 'Navamsha strength, dharma and deeper planetary potential.',
        roleHi: 'दांपत्य, सूक्ष्म ग्रहीय बल, धर्म एवं भाग्योदय।'
      };
    case 'D10':
      return {
        roleEn: 'Vocational karma, authority and professional status.',
        roleHi: 'आजीविका, पद, प्रतिष्ठा, कर्म एवं सामाजिक प्रभाव।'
      };
    case 'D7':
      return {
        roleEn: 'Progeny karma, children and creative lineage.',
        roleHi: 'संतान सुख, वंश वृद्धि व रचनात्मक प्रतिभा।'
      };
    case 'D4':
      return {
        roleEn: 'Fixed assets, property, residence and real estate luck.',
        roleHi: 'अचल संपत्ति, भूमि, गृह व पारिवारिक सुख।'
      };
    case 'D16':
      return {
        roleEn: 'Vehicles, conveyance and material luxuries.',
        roleHi: 'वाहन सुख, यात्रा साधन व भौतिक ऐश्वर्य।'
      };
    case 'D24':
      return {
        roleEn: 'Higher learning, intellect and academic achievements.',
        roleHi: 'उच्च विद्या, शोध, ज्ञानार्जन व बौद्धिक कौशल।'
      };
    case 'D30':
      return {
        roleEn: 'Arishta, health vulnerabilities and protective karma.',
        roleHi: 'अनिष्ट, रोग, बाधाएं व संकट निवारण क्षमता।'
      };
    case 'D60':
      return {
        roleEn: 'Past-life karma, deep prarabdha and ultimate truth.',
        roleHi: 'पूर्व जन्म के संचित कर्म व गूढ़ प्रारब्ध।'
      };
    case 'D2':
      return {
        roleEn: 'Wealth accumulation, liquid assets and family treasury.',
        roleHi: 'धन संचय, चल संपत्ति व वित्तीय स्थिरता।'
      };
    case 'D3':
      return {
        roleEn: 'Siblings, courage, vitality and sustained initiative.',
        roleHi: 'भाई-बहन, उद्यम, पराक्रम व धैर्य।'
      };
    case 'D12':
      return {
        roleEn: 'Parents, ancestral lineage and genetic heritage.',
        roleHi: 'माता-पिता, पितृ कुल व पैतृक संस्कार।'
      };
    default:
      return {
        roleEn: 'Divisional harmonic examining specific karma dimension.',
        roleHi: 'विभागीय सूक्ष्म वर्ग चक्र विश्लेषण।'
      };
  }
}
